import React, { InputHTMLAttributes, Ref, useRef, useState } from 'react';

import { cx } from '@emotion/css';
import { Popover, Transition } from '@headlessui/react';
import { ExclamationCircleIcon } from '@heroicons/react/20/solid';
import {
  CheckIcon,
  DocumentDuplicateIcon,
  LinkIcon,
} from '@heroicons/react/24/outline';

import { copyToClipboard } from '@/helpers/strings';
import IInput from '@/types/IInput';

import Copy from './Icons/Copy';

export default function Input({
  autocomplete,
  inputMode,
  pattern,
  className,
  copy,
  description,
  disabled,
  error,
  icon,
  id,
  label,
  message,
  onChange,
  onFocus,
  onBlur,
  onKeyDown,
  placeholder,
  preLabel,
  required,
  type,
  value,
  min,
  max,
  maxLength,
  rightIcon,
  handleRightIconClick,
}: IInput) {
  return (
    <div>
      <label
        htmlFor={id || label}
        className={cx(
          className,
          'block text-sm leading-5 font-medium',
          'text-gray-900',
        )}
      >
        {label}
      </label>
      {description && (
        <p className="mt-2 text-sm text-gray-500">{description}</p>
      )}
      <div
        className={cx('relative flex rounded-md', label === '' ? '' : 'mt-1')}
      >
        {preLabel && (
          <span className="inline-flex items-center px-3 rounded-l-md border border-r-0 border-gray-300 bg-gray-50 text-gray-500 text-sm">
            {preLabel}
          </span>
        )}
        {icon && (
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-gray-400">
            {React.createElement(icon, { className: 'h-5 w-5' })}
          </div>
        )}
        <div className="w-full flex">
          <div className="flex-1">
            <input
              id={id || label}
              name={id || label}
              type={type}
              autoComplete={autocomplete || type}
              required={required}
              placeholder={placeholder}
              className={cx(
                'appearance-none transition-all flex-1 block w-full px-3 py-2 border shadow-sm placeholder-gray-500 text-gray-900 focus:outline-none disabled:bg-gray-200 disabled:cursor-not-allowed',
                preLabel ? 'rounded-r-md' : 'rounded-md',
                error
                  ? 'text-red-900 border-red-300 focus:ring-red-500 focus:border-red-500'
                  : 'border-gray-300 focus:ring-brand-500 focus:border-brand-500',
                icon ? 'text-sm leading-5 pl-10' : 'text-base leading-6',
                value === 'Click to edit or clear'
                  ? 'bg-gray-50 text-gray-700'
                  : '',
              )}
              min={min}
              max={max}
              value={value}
              onChange={e => onChange(e.target.value)}
              onFocus={onFocus}
              onBlur={onBlur}
              onKeyDown={onKeyDown}
              disabled={disabled}
              maxLength={maxLength}
              inputMode={inputMode}
              pattern={pattern}
            />
          </div>
          {rightIcon && !disabled && (
            <div
              tabIndex={0}
              role="button"
              className="flex items-center ml-2 min-w-fit"
              onClick={() => {
                handleRightIconClick?.();
              }}
              onKeyDown={() => {
                handleRightIconClick?.();
              }}
            >
              {rightIcon}
            </div>
          )}
        </div>
        {error && (
          <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
            <ExclamationCircleIcon
              className="h-5 w-5 text-red-500"
              aria-hidden="true"
            />
          </div>
        )}
        {value && copy && (
          <Popover className="absolute inset-y-0 right-0 pr-3 flex items-center">
            <Popover.Button as="div" className="h-5 w-5">
              <button
                type="button"
                className="appearance-none"
                onClick={() => {
                  copyToClipboard(value);
                }}
              >
                <Copy className="h-5 w-5" aria-hidden="true" />
              </button>
            </Popover.Button>

            <Transition
              enter="transition-opacity duration-100 ease-out"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="transition-opacity duration-75 ease-out"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <Popover.Panel className="absolute z-10 -top-6 leading-3 right-0 text-xs text-emerald-700 bg-white w-20 text-center">
                {({ close }) => {
                  setTimeout(() => {
                    close();
                  }, 2000);
                  return (
                    <div className="flex items-center justify-end">
                      <CheckIcon className="h-5 w-5 mr-1" /> Copied
                    </div>
                  );
                }}
              </Popover.Panel>
            </Transition>
          </Popover>
        )}
      </div>
      {message && (
        <p className={cx(className, 'mt-2 text-sm leading-5', 'text-gray-500')}>
          {message}
        </p>
      )}
    </div>
  );
}

// this is basically an updated version of the Input component with ALL props of that of the original input html tag
// should use this everywhere moving forward
type FormInputProps = {
  label: string;
  error?: boolean;
  ref?: Ref<HTMLInputElement>;
} & InputHTMLAttributes<HTMLInputElement>;

export function FormInput({ ref, ...props }: FormInputProps) {
  const { label, error, id, name, ...rest } = props;

  const inputRef = useRef<HTMLInputElement | null>(null);

  return (
    <div className="space-y-1">
      <label
        htmlFor={id || label}
        className="text-sm leading-5 font-medium text-gray-900"
      >
        {label}
        {rest.required ? <span className="text-red-400"> *</span> : null}
      </label>
      <input
        id={id || label}
        name={name || label}
        ref={node => {
          inputRef.current = node;
          if (typeof ref === 'function') {
            ref(node);
          } else if (ref) {
            ref.current = node;
          }
        }}
        className={cx(
          'appearance-none transition-all flex-1 w-full px-3 py-2 border shadow-sm placeholder-gray-500 text-gray-900 focus:outline-none disabled:bg-gray-200 disabled:cursor-not-allowed rounded-md',
          error
            ? 'border-red-300 text-red-900 focus:ring-red-500 focus:border-red-500'
            : 'border-gray-300 focus:ring-brand-500 focus:border-brand-500',
        )}
        {...rest}
      />
    </div>
  );
}

type CopyableTextProps = {
  value: string;
  copy: string;
};

export function CopyableTextInput({ value, copy }: CopyableTextProps) {
  const [isCopied, setIsCopied] = useState(false);

  function copyValue() {
    copyToClipboard(copy, () => {
      setIsCopied(true);
      setTimeout(() => {
        setIsCopied(false);
      }, 4000);
    });
  }

  return (
    <div className="mt-1 flex rounded-md shadow-sm">
      <div className="relative flex flex-grow items-stretch focus-within:z-10 rounded-l-md border border-gray-300">
        <div className="h-full flex items-center pl-2">
          <LinkIcon className="w-5 self-center text-gray-400" />
        </div>
        <input
          type="text"
          name="entity_id"
          id="entity_id"
          className="block w-full border-none rounded-none focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
          value={value}
          disabled
        />
      </div>
      <button
        type="button"
        className="relative -ml-px inline-flex items-center space-x-2 rounded-r-md border border-gray-300 bg-gray-50 p-2 text-sm font-medium text-gray-700 hover:bg-gray-100 focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
        onClick={copyValue}
        disabled={isCopied}
      >
        {isCopied ? (
          'Copied!'
        ) : (
          <DocumentDuplicateIcon
            className="h-5 w-5 text-gray-400"
            aria-hidden="true"
          />
        )}
      </button>
    </div>
  );
}
